import React from 'react'
import PropTypes from 'prop-types'
import DisplayPrice from '../../display-price'
import ImageList, {Skeleton as ImageListSkeleton} from '../../image-list'
import {HideOnDesktop} from '@salesforce/retail-react-app/app/components/responsive'
import {Box, Flex, Skeleton, Stack, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import ProductLikes from '../../product-tile/product-likes'
import {productUrlBuilder, rebuildPathWithParams} from '../../../utils/url'

const QuickViewMobileHeader = ({
    styles,
    showSkeleton,
    showImageGallery,
    imageSize,
    product,
    variationParams,
    isProductPartOfSet,
    isProductPartOfBundle,
    isWishlistEnabled,
    isFavourite,
    wishlistCount,
    priceData,
    searchHitData,
    forceMobileLayout = false
}) => {
    const showHeaderSkeleton = showSkeleton || !product
    const productUrl = product
        ? rebuildPathWithParams(productUrlBuilder(product), variationParams || {})
        : null
    const content = (
        <Stack {...styles.mobileHeaderStack}>
            {showImageGallery && (
                <Box {...styles.mobileHeaderImageWrapper}>
                    {!showHeaderSkeleton ? (
                        <ImageList
                            size={imageSize}
                            imageGroups={product?.imageGroups}
                            selectedVariationAttributes={variationParams}
                            lazy={isProductPartOfSet || isProductPartOfBundle}
                        />
                    ) : (
                        <ImageListSkeleton />
                    )}
                </Box>
            )}
            <Box {...styles.mobileHeaderTextWrap}>
                <Flex {...styles.mobileHeaderTitleRow}>
                    {!showHeaderSkeleton ? (
                        <Text {...styles.mobileHeaderTitle}>
                            {product?.name && productUrl ? (
                                <Link
                                    to={productUrl}
                                    data-testid="quick-view-mobile-header-product-link"
                                    color="inherit"
                                    textDecoration="none"
                                    _hover={{textDecoration: 'underline'}}
                                    _focusVisible={{outline: '2px solid', outlineOffset: '2px'}}
                                >
                                    <Box as='span' {...styles.mobileHeaderTitle}>
                                        {product.name}
                                    </Box>
                                </Link>
                            ) : (
                                product?.name
                            )}
                        </Text>
                    ) : (
                        <Skeleton {...styles.mobileHeaderTitleSkeleton} />
                    )}
                    {isWishlistEnabled && (
                        <Flex {...styles.mobileHeaderLikes}>
                            {!showHeaderSkeleton ? (
                                <ProductLikes
                                    isFavourite={isFavourite}
                                    wishlistCount={wishlistCount}
                                    variant="QuickView"
                                />
                            ) : (
                                <Skeleton {...styles.mobileHeaderLikesSkeleton} />
                            )}
                        </Flex>
                    )}
                </Flex>
                <Box>
                    {!showHeaderSkeleton && priceData?.currentPrice ? (
                        <DisplayPrice
                            priceData={priceData}
                            currency={product?.currency || searchHitData?.currency}
                            listPriceProps={styles.mobileListPrice}
                            currentPriceProps={styles.mobileCurrentPrice}
                        />
                    ) : (
                        <Skeleton {...styles.mobileHeaderPriceSkeleton} />
                    )}
                </Box>
            </Box>
        </Stack>
    )

    return forceMobileLayout ? (
        content
    ) : (
        <HideOnDesktop>
            {content}
        </HideOnDesktop>
    )
}

QuickViewMobileHeader.propTypes = {
    styles: PropTypes.object,
    showSkeleton: PropTypes.bool,
    showImageGallery: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    product: PropTypes.object,
    variationParams: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    isWishlistEnabled: PropTypes.bool,
    isFavourite: PropTypes.bool,
    wishlistCount: PropTypes.number,
    priceData: PropTypes.object,
    searchHitData: PropTypes.object,
    forceMobileLayout: PropTypes.bool
}

export default QuickViewMobileHeader
